import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform, Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { BIRD_X, BIRD_WIDTH, BIRD_HEIGHT, PIPE_WIDTH, createFlight, flap, stepFlight } from '@/lib/waitingFlappy';

const createStyles = colors => StyleSheet.create({
  game: { alignItems: 'center', gap: 8, maxWidth: '100%' },
  header: { width: '100%', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  smallButton: { minHeight: 32, minWidth: 44, paddingHorizontal: 8, alignItems: 'center', justifyContent: 'center', borderRadius: 8 },
  field: { borderRadius: 12, overflow: 'hidden', backgroundColor: colors.solid_color_primary },
  tapArea: { ...StyleSheet.absoluteFillObject },
  pipe: { position: 'absolute', width: PIPE_WIDTH, backgroundColor: colors.text_color_secondary, borderRadius: 4 },
  bird: { position: 'absolute', left: BIRD_X, width: BIRD_WIDTH, height: BIRD_HEIGHT, borderRadius: 10, backgroundColor: colors.text_color_primary },
  wing: { position: 'absolute', left: 3, top: 8, width: 9, height: 5, borderRadius: 4, backgroundColor: colors.solid_color_primary, opacity: 0.55 },
  eye: { position: 'absolute', right: 4, top: 4, width: 3, height: 3, borderRadius: 2, backgroundColor: colors.solid_color_primary },
  beak: { position: 'absolute', right: -4, top: 9, width: 6, height: 4, borderRadius: 2, backgroundColor: colors.text_color_primary },
  hint: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 16, pointerEvents: 'none' },
  overlay: { ...StyleSheet.absoluteFillObject, backgroundColor: colors.solid_color_primary, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 16 },
  description: { textAlign: 'center' },
  playButton: { minHeight: 44, paddingHorizontal: 20, borderRadius: 10, backgroundColor: colors.text_color_primary, alignItems: 'center', justifyContent: 'center' },
});

function WaitingFlappyGame({ colors, compact = false, fieldWidth, onHide }) {
  const styles = useMemo(() => createStyles(colors), [colors]);
  const width = fieldWidth ?? (compact ? 216 : 280);
  const height = compact ? 176 : 220;
  const [phase, setPhase] = useState('ready');
  const [flight, setFlight] = useState(() => createFlight(width, height));
  const flightRef = useRef(flight);
  const boardRef = useRef(null);
  const [best, setBest] = useState(0);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const pause = useCallback(() => setPhase(current => current === 'playing' ? 'paused' : current), []);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', state => { if (state !== 'active') pause(); });
    const onVisibility = () => { if (document.hidden) pause(); };
    if (Platform.OS === 'web') document.addEventListener('visibilitychange', onVisibility);
    return () => {
      subscription.remove();
      if (Platform.OS === 'web') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pause]);

  useEffect(() => {
    // Resizing pauses the round instead of moving a running bird into a wall.
    const next = createFlight(width, height);
    flightRef.current = next;
    setFlight(next);
    setPhase(current => current === 'ready' ? current : 'paused');
  }, [width, height]);

  useEffect(() => {
    if (phase !== 'playing') return undefined;
    boardRef.current?.focus?.();
    let lastFrame = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      const next = stepFlight(flightRef.current, (now - lastFrame) / 1000, width, height);
      lastFrame = now;
      flightRef.current = next;
      setFlight(next);
      setBest(current => Math.max(current, next.score));
      if (next.ended) setPhase('ended');
    }, 33);
    return () => clearInterval(timer);
  }, [phase, width, height]);

  const fly = () => {
    if (phase !== 'playing') return;
    const next = flap(flightRef.current);
    flightRef.current = next;
    setFlight(next);
  };
  const onKeyDown = event => {
    const native = event.nativeEvent || {};
    const key = native.key || event.key || (native.keyCode === 49 ? ' ' : '');
    if (key === ' ' || key === 'ArrowUp' || key === 'Up') {
      event.preventDefault?.();
      setKeyboardFocus(true);
      if (!event.repeat && !native.repeat) fly();
    }
  };
  const play = () => {
    if (phase !== 'paused') {
      const next = createFlight(width, height);
      flightRef.current = next;
      setFlight(next);
    }
    setPhase('playing');
  };

  return (
    <View style={styles.game} testID="waiting-flappy-game">
      <View style={styles.header}>
        <Text size="xs" weight="medium">Score · {flight.score} <Text size="xxs" color="secondary">Best {best}</Text></Text>
        <View style={styles.actions}>
          {phase === 'playing' && <Pressable accessibilityRole="button" accessibilityLabel="Pause Flappy Flight" onPress={pause} style={styles.smallButton} hitSlop={6}><Icon name="pause" size={12} color={colors.text_color_secondary} /></Pressable>}
          <Pressable accessibilityRole="button" accessibilityLabel="Hide Flappy Flight" onPress={onHide} style={styles.smallButton} hitSlop={6}><Text size="xs" color="secondary">Games</Text></Pressable>
        </View>
      </View>
      <View testID="waiting-flappy-field" style={[styles.field, { width, height }]}>
        {flight.pipes.map(pipe => <React.Fragment key={pipe.id}>
          <View style={[styles.pipe, { left: pipe.x, top: 0, height: pipe.top }]} />
          <View style={[styles.pipe, { left: pipe.x, top: pipe.top + pipe.gap, bottom: 0 }]} />
        </React.Fragment>)}
        <View testID="waiting-flappy-bird" style={[styles.bird, { top: flight.y }]}><View style={styles.wing} /><View style={styles.eye} /><View style={styles.beak} /></View>
        {phase === 'playing' && <>
          {!flight.flying && <View style={styles.hint}><Text size="xs" color="secondary" style={styles.description}>Tap to take flight</Text></View>}
          <Pressable ref={boardRef} accessibilityRole="button" accessibilityLabel="Flap your wings" onPress={fly}
            {...(['web', 'macos'].includes(Platform.OS) ? { onKeyDown } : {})}
            {...(Platform.OS === 'macos' ? { keyDownEvents: [{ key: ' ' }, { key: 'ArrowUp' }, { key: 'Up' }], enableFocusRing: false } : {})}
            onFocus={event => setKeyboardFocus(!!event.nativeEvent?.target?.matches?.(':focus-visible'))} onBlur={() => setKeyboardFocus(false)}
            style={[styles.tapArea, Platform.OS === 'web' && { outlineStyle: 'none' }, keyboardFocus && { borderWidth: 1, borderColor: colors.text_color_secondary, borderRadius: 12 }]} />
        </>}
        {phase !== 'playing' && <View style={styles.overlay}>
          <Text size="m" weight="medium" accessibilityLiveRegion="polite">{phase === 'ready' ? 'Flappy Flight' : phase === 'paused' ? 'Paused' : `Nice flight! Score: ${flight.score}`}</Text>
          {phase === 'ready' && <Text size="xs" color="secondary" style={styles.description}>Tap to fly through the gaps.</Text>}
          <Pressable accessibilityRole="button" accessibilityLabel={phase === 'paused' ? 'Resume Flappy Flight' : phase === 'ended' ? 'Play Flappy Flight again' : 'Start Flappy Flight'} onPress={play} style={styles.playButton}>
            <Text size="s" weight="medium" style={{ color: colors.accent_text_color }}>{phase === 'paused' ? 'Resume' : phase === 'ended' ? 'Play again' : 'Let’s play'}</Text>
          </Pressable>
        </View>}
      </View>
      <Text size="xxs" color="secondary">Tap, click or press Space to fly</Text>
    </View>
  );
}

export default React.memo(WaitingFlappyGame);
